/**
 * Standard Supply — Members Club discount engine.
 *
 * Picks the best applicable discount code (per-product multibuy vs customer tier),
 * applies it via Shopify's /discount/CODE endpoint, refreshes the cart UI.
 *
 * Inputs (set via snippets/ss-discount-engine-bootstrap.liquid):
 *   window.SS_DISCOUNT_CONFIG = {
 *     tier: 'member' | 'plus' | 'premium' | 'mvp' | '',
 *     products: { '<product-handle>': '<code-slug>', ... }
 *   }
 *
 * Codes assumed to exist in admin:
 *   MB_<slug>_3, MB_<slug>_5, MB_<slug>_10  (per product, 10/15/20%)
 *   TIER_MEMBER, TIER_PLUS, TIER_PREMIUM, TIER_MVP (segment-gated, 5/10/15/20% off Essentials)
 */
(function () {
  'use strict';

  var cfg = (typeof window !== 'undefined' && window.SS_DISCOUNT_CONFIG) || {};
  var PRODUCTS = cfg.products || {};
  var TIER = (cfg.tier || '').toLowerCase();

  var TIER_PCT = { member: 0.05, plus: 0.10, premium: 0.15, mvp: 0.20 };
  var MULTIBUY = [
    { min: 10, pct: 0.20 },
    { min: 5,  pct: 0.15 },
    { min: 3,  pct: 0.10 }
  ];

  var lastAppliedCode = null;
  var inFlight = false;
  var loopGuard = false;

  function evaluate(cart) {
    var candidates = [];
    var perProduct = {};

    (cart.items || []).forEach(function (item) {
      var slug = PRODUCTS[item.handle];
      if (!slug) return;
      var bucket = perProduct[item.handle] || { slug: slug, qty: 0, totalCents: 0 };
      bucket.qty += (item.quantity || 0);
      bucket.totalCents += (item.original_line_price || 0);
      perProduct[item.handle] = bucket;
    });

    Object.keys(perProduct).forEach(function (h) {
      var p = perProduct[h];
      for (var i = 0; i < MULTIBUY.length; i++) {
        var rule = MULTIBUY[i];
        if (p.qty >= rule.min) {
          candidates.push({
            code: 'MB_' + p.slug + '_' + rule.min,
            savingsCents: Math.round(p.totalCents * rule.pct),
            kind: 'multibuy'
          });
          break;
        }
      }
    });

    if (TIER && TIER_PCT[TIER]) {
      var essentialCents = 0;
      (cart.items || []).forEach(function (item) {
        if (PRODUCTS[item.handle]) essentialCents += (item.original_line_price || 0);
      });
      if (essentialCents > 0) {
        candidates.push({
          code: 'TIER_' + TIER.toUpperCase(),
          savingsCents: Math.round(essentialCents * TIER_PCT[TIER]),
          kind: 'tier'
        });
      }
    }

    if (!candidates.length) return null;
    candidates.sort(function (a, b) { return b.savingsCents - a.savingsCents; });
    return candidates[0];
  }

  function isOurCode(code) {
    return typeof code === 'string' && /^(MB_|TIER_)/.test(code);
  }

  function applyDiscount(code) {
    return fetch('/discount/' + encodeURIComponent(code), {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'manual'
    }).catch(function () {});
  }

  async function reconcile() {
    if (inFlight) return;
    inFlight = true;
    try {
      var resp = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!resp.ok) return;
      var cart = await resp.json();
      if (!cart || !Array.isArray(cart.items)) return;

      var appliedCodes = (cart.discount_codes || []).map(function (d) { return (d && d.code) || ''; }).filter(Boolean);
      var hasUserCode = appliedCodes.some(function (c) { return !isOurCode(c); });
      if (hasUserCode) return; // respect customer's own code

      var winner = evaluate(cart);
      if (!winner) {
        // No applicable code. If an old MB_/TIER_ is still attached, leave it —
        // Shopify will simply compute $0 savings and the customer can clear via UI if they care.
        return;
      }

      var currentOurCode = appliedCodes.find(isOurCode);
      if (currentOurCode === winner.code) return;

      loopGuard = true;
      await applyDiscount(winner.code);
      lastAppliedCode = winner.code;

      // Tell the theme cart drawer to re-render via section rendering API
      try {
        var sectionsResp = await fetch('/cart.js', { credentials: 'same-origin' });
        await sectionsResp.json();
      } catch (e) { /* noop */ }

      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'ss-discount-engine', code: winner.code } }));
    } catch (e) {
      if (window && window.console) console.warn('[ss-discount-engine] reconcile error', e);
    } finally {
      inFlight = false;
      setTimeout(function () { loopGuard = false; }, 250);
    }
  }

  document.addEventListener('cart:update', function (e) {
    if (loopGuard) return;
    if (e && e.detail && e.detail.source === 'ss-discount-engine') return;
    setTimeout(reconcile, 150);
  });

  // Also fire on cart drawer open + initial load (covers themes that don't dispatch cart:update reliably)
  document.addEventListener('cart-drawer:open', function () { setTimeout(reconcile, 100); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(reconcile, 300); });
  } else {
    setTimeout(reconcile, 300);
  }

  // Expose for manual debug
  window.SSDiscount = { reconcile: reconcile, evaluate: evaluate };
})();
